import { SUPPORTED_LOCALES } from "@/lib/locales";
import type { SupportedLocale } from "@/lib/locales";
import {
  buildItemDescriptionTranslations,
  buildOptionalTranslations,
  buildRequiredTranslations,
  buildSparseTranslations,
  findMissingTranslations,
  hasPartialTranslations,
  type TranslationFormValues,
} from "@/lib/translationResolution";
import { storagePathFromValue } from "@/lib/storageFetch";
import type {
  CarouselSlide,
  CarouselSlideFields,
} from "@/services/carouselSlideService";
import { ACTIVITY_TYPE_ORDER } from "@/constants";
import type { ActivityCategory } from "@/types";

export const CAROUSEL_SLIDE_FORM_ID = "carousel-slide-form";

/** List lives under Devices → Carousel tab. */
export const CAROUSEL_LIST_PATH = "/devices/carousel";

export type TargetingMode = "all" | "selected";
export type SaveMode = "draft" | "publish";

export type FormState = {
  title: TranslationFormValues;
  description: TranslationFormValues;
  activityId: string | null;
  showActivityButton: boolean;
  ignoreDistance: boolean;
  categories: ActivityCategory[];
  targeting: TargetingMode;
  houseIds: string[];
  isEnabled: boolean;
  startsAt: string;
  endsAt: string;
  imagePath: string | null;
};

function toFormTranslations(
  value: TranslationFormValues | null,
): TranslationFormValues {
  return Object.fromEntries(
    SUPPORTED_LOCALES.map((code) => [code, value?.[code] ?? ""]),
  ) as TranslationFormValues;
}

export function toDatetimeLocalValue(iso: string | null): string {
  if (!iso) return "";
  const date = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function fromDatetimeLocalValue(value: string): string | null {
  if (!value) return null;
  return new Date(value).toISOString();
}

export function emptyFormState(): FormState {
  return {
    title: toFormTranslations(null),
    description: toFormTranslations(null),
    activityId: null,
    showActivityButton: false,
    ignoreDistance: false,
    categories: [...ACTIVITY_TYPE_ORDER],
    targeting: "all",
    houseIds: [],
    isEnabled: true,
    startsAt: "",
    endsAt: "",
    imagePath: null,
  };
}

export function formFromSlide(slide: CarouselSlide): FormState {
  return {
    title: toFormTranslations(slide.title_translations),
    description: toFormTranslations(slide.description_translations),
    activityId: slide.activity_id,
    showActivityButton: slide.show_activity_button,
    ignoreDistance: slide.ignore_distance,
    categories: slide.categories,
    targeting: slide.house_ids.length === 0 ? "all" : "selected",
    houseIds: slide.house_ids,
    isEnabled: slide.is_enabled,
    startsAt: toDatetimeLocalValue(slide.starts_at),
    endsAt: toDatetimeLocalValue(slide.ends_at),
    imagePath: slide.image_path,
  };
}

export function serializeFormState(form: FormState): string {
  return JSON.stringify({
    ...form,
    categories: [...form.categories].sort(),
    houseIds: [...form.houseIds].sort(),
  });
}

export type FormErrors = {
  title?: string;
  description?: string;
  photo?: string;
  categories?: string;
  structures?: string;
};

export type ValidateSlideFormMessages = {
  titleRequiredDraft: string;
  titleRequired: string;
  descriptionPartial: string;
  photoRequired: string;
  categoriesRequired: string;
  structuresRequired: string;
};

export type ValidateSlideFormResult =
  | { ok: true; incompleteTranslations?: false }
  | { ok: true; incompleteTranslations: true }
  | { ok: false; errors: FormErrors };

export function validateSlideForm(
  snapshot: FormState,
  mode: SaveMode,
  messages: ValidateSlideFormMessages,
  options: {
    isCopyLocaleComplete: (code: SupportedLocale) => boolean;
    translateSourceLanguage: SupportedLocale;
  },
): ValidateSlideFormResult {
  const { isCopyLocaleComplete, translateSourceLanguage } = options;
  const hasAnyTitle = SUPPORTED_LOCALES.some((code) =>
    Boolean(snapshot.title[code]?.trim()),
  );
  const hasCategories = snapshot.categories.length > 0;
  const missingStructures =
    snapshot.targeting === "selected" && snapshot.houseIds.length === 0;

  if (mode === "draft") {
    const errors: FormErrors = {};
    if (!hasAnyTitle) errors.title = messages.titleRequiredDraft;
    if (!hasCategories) errors.categories = messages.categoriesRequired;
    if (missingStructures) errors.structures = messages.structuresRequired;
    if (Object.keys(errors).length > 0) return { ok: false, errors };
    return { ok: true };
  }

  const titleMissing = findMissingTranslations(snapshot.title).length > 0;
  const descriptionPartial = hasPartialTranslations(snapshot.description);
  const missingPhoto = !snapshot.imagePath && !snapshot.activityId;
  const errors: FormErrors = {};
  if (missingPhoto) errors.photo = messages.photoRequired;
  if (!hasCategories) errors.categories = messages.categoriesRequired;
  if (missingStructures) errors.structures = messages.structuresRequired;

  if (missingPhoto || !hasCategories || missingStructures) {
    if (titleMissing) errors.title = messages.titleRequired;
    if (descriptionPartial) errors.description = messages.descriptionPartial;
    return { ok: false, errors };
  }

  if (titleMissing || descriptionPartial) {
    if (!isCopyLocaleComplete(translateSourceLanguage)) {
      if (!snapshot.title[translateSourceLanguage]?.trim()) {
        errors.title = messages.titleRequiredDraft;
      }
      if (
        descriptionPartial &&
        !snapshot.description[translateSourceLanguage]?.trim()
      ) {
        errors.description = messages.descriptionPartial;
      }
      return { ok: false, errors };
    }
    return { ok: true, incompleteTranslations: true };
  }

  return { ok: true };
}

export function fieldsFromForm(
  snapshot: FormState,
  mode: SaveMode,
): CarouselSlideFields {
  const asDraft = mode === "draft";
  const activity_id = snapshot.activityId;
  return {
    title_translations: asDraft
      ? buildSparseTranslations(snapshot.title)
      : buildRequiredTranslations(snapshot.title),
    description_translations: asDraft
      ? buildOptionalTranslations(snapshot.description)
      : buildItemDescriptionTranslations(snapshot.description),
    image_path: snapshot.imagePath
      ? storagePathFromValue(snapshot.imagePath)
      : null,
    activity_id,
    show_activity_button: Boolean(activity_id) && snapshot.showActivityButton,
    ignore_distance: Boolean(activity_id) && snapshot.ignoreDistance,
    categories: snapshot.categories,
    house_ids: snapshot.targeting === "all" ? [] : snapshot.houseIds,
    is_enabled: snapshot.isEnabled,
    is_draft: asDraft,
    starts_at: fromDatetimeLocalValue(snapshot.startsAt),
    ends_at: fromDatetimeLocalValue(snapshot.endsAt),
  };
}
